import {AssetType} from '../../enums/asset-type';
import {Security} from '../../models/security';
import {SymbolDecimalsInputParams, SymbolDecimalsService, SymbolFormats} from '../symbol-decimals';
import {CachedSymbolFormats} from '../symbol-decimals/cached-symbol-formats';
import {getOrderQtyAssetType, getOrderQtyCurrency, OrderOrReportForQtyMultiCurrency} from './order-qty-multi-currency';
import {OrderQtyDecimalsInputParams, OrderQtyMultiCurrencyInfo} from './order-qty-multi-currency-info';

/**
 * It's used to "disable" qty step validation, e.g. when closing a position.
 * Why not to use "storeScale":
 * For OKEx exchange, qty in position can have more decimal digits,
 * than corresponding scale field.
 */
const MAX_QTY_SCALE = 12;
const MIN_STEP = Math.pow(10, -MAX_QTY_SCALE);

const innerGetOrderQtyDecimalsInputParams = (
    securityDecimalsInputParams: SymbolDecimalsInputParams,
    qtyAssetType: AssetType,
    baseQtyAssetType: AssetType,
    fixedDecimalParamsByAssetType: Partial<Record<AssetType, OrderQtyDecimalsInputParams>>,
    isMinStepNeeded: boolean = false
): OrderQtyDecimalsInputParams => {
    const getNormalQtyParams = (field: 'quantity' | 'price'): OrderQtyDecimalsInputParams => ({
        decimalCount:      securityDecimalsInputParams.decimals[field],
        decimalStoreCount: securityDecimalsInputParams.decimals.store[field],
        step:              isMinStepNeeded
                               ? MIN_STEP
                               : securityDecimalsInputParams.steps[field],
        min:               securityDecimalsInputParams.min[field],
        max:               securityDecimalsInputParams.max[field],
        precision:         securityDecimalsInputParams.precision[field]
    });

    return fixedDecimalParamsByAssetType
        && fixedDecimalParamsByAssetType[qtyAssetType]
        || (
            !baseQtyAssetType

            /**
             * [HED-800] qtyScale is always for base asset, regardless of {@link baseQtyAssetType},
             * for example it's true for OKEx.
             */
            || qtyAssetType === AssetType.BASE
                ? getNormalQtyParams('quantity')
                : getNormalQtyParams('price')
        );
};

export const getOrderQtyDecimalsInputParams = (
    {
        qtyMultiCurrencyInfo,
        securityInputParams,
        toNativeQtyAssetType,
        qtyAssetType,
        isMinStepNeeded
    }: {
        qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo;
        securityInputParams: SymbolDecimalsInputParams;
        toNativeQtyAssetType?: boolean;
        qtyAssetType?: AssetType;
        isMinStepNeeded?: boolean;
    }): OrderQtyDecimalsInputParams => {

    let result: OrderQtyDecimalsInputParams;

    if (qtyMultiCurrencyInfo.isMultiCurrencyAllowed) {
        qtyAssetType = (toNativeQtyAssetType && !qtyMultiCurrencyInfo.isMultiCurrencyNativelySupported)
            ? qtyMultiCurrencyInfo.baseQtyAssetType
            : qtyAssetType || qtyMultiCurrencyInfo.baseQtyAssetType;

        result = innerGetOrderQtyDecimalsInputParams(
            securityInputParams,
            qtyAssetType,
            qtyMultiCurrencyInfo.baseQtyAssetType,
            qtyMultiCurrencyInfo.fixedDecimalParamsByAssetType,
            isMinStepNeeded
        );
    } else {
        result = {
            min:               securityInputParams.min.quantity,
            max:               securityInputParams.max.quantity,
            step:              isMinStepNeeded
                                   ? MIN_STEP
                                   : securityInputParams.steps.quantity,
            decimalCount:      securityInputParams.decimals.quantity,
            decimalStoreCount: securityInputParams.decimals.store.quantity,
            precision:         securityInputParams.precision.quantity
        };
    }

    return result;
};

export const getOrderQtyCorrectedSecurityInputParams = (
    {
        qtyMultiCurrencyInfo,
        securityInputParams,
        toNativeQtyAssetType,
        qtyAssetType,
        isMinStepNeeded
    }: {
        qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo;
        securityInputParams: SymbolDecimalsInputParams;
        toNativeQtyAssetType?: boolean;
        qtyAssetType?: AssetType;
        isMinStepNeeded?: boolean;
    }
): SymbolDecimalsInputParams => {
    const qtyParams = getOrderQtyDecimalsInputParams({
        qtyMultiCurrencyInfo,
        securityInputParams,
        toNativeQtyAssetType,
        qtyAssetType,
        isMinStepNeeded
    });

    return {
        decimals:  {
            quantity: qtyParams.decimalCount,
            report:   securityInputParams.decimals.report,
            price:    securityInputParams.decimals.price,
            store:    {
                ...securityInputParams.decimals.store,
                quantity: qtyParams.decimalStoreCount
            }
        },
        steps:     {
            quantity: qtyParams.step,
            report:   securityInputParams.steps.report,
            price:    securityInputParams.steps.price,
            store:    securityInputParams.steps.store
        },
        min:       {
            price:    securityInputParams.min.price,
            quantity: qtyParams.min,
            notional: securityInputParams.min.notional
        },
        max:       {
            price:    securityInputParams.max.price,
            quantity: qtyParams.max
        },
        precision: {
            price:    securityInputParams.precision.price,
            quantity: qtyParams.precision,
            report:   securityInputParams.precision.report
        }
    };
};

export const getOrderQtyCorrectedSecurityFormats = (
    {
        security,
        qtyMultiCurrencyInfo,
        securityInputParams,
        normalSecurityFormats,
        toNativeQtyAssetType,
        qtyAssetType
    }: {
        security: Security;
        qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo;
        securityInputParams: SymbolDecimalsInputParams;
        normalSecurityFormats: SymbolFormats;
        toNativeQtyAssetType?: boolean;
        qtyAssetType?: AssetType;
    }): SymbolFormats => {

    let result: SymbolFormats;

    if (qtyMultiCurrencyInfo.isMultiCurrencyAllowed) {
        const inputParams = getOrderQtyCorrectedSecurityInputParams({
            qtyMultiCurrencyInfo,
            securityInputParams,
            toNativeQtyAssetType,
            qtyAssetType
        });

        result = new CachedSymbolFormats(
            security,
            inputParams.decimals,
            inputParams.precision
        );
    } else {
        result = normalSecurityFormats;
    }

    return result;
};

export interface OrderQtyFormats {
    formats: SymbolFormats;
    baseFormats: SymbolFormats;
    currencySymbol: string;
    baseCurrencySymbol: string;
    qtyAssetType: AssetType;
    qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo;
    isQtyInBaseQtyAsset: boolean;
}

export function getOrderQtyFormats(
    {
        security,
        securityFormats,
        securityInputParams,
        symbolDecimalsService,
        qtyMultiCurrencyInfo,
        orderOrReport,
        qtyAssetType
    }: {
        security: Security;
        securityFormats?: SymbolFormats;
        securityInputParams?: SymbolDecimalsInputParams;
        symbolDecimalsService?: SymbolDecimalsService;
        qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo;
        orderOrReport?: OrderOrReportForQtyMultiCurrency;
        qtyAssetType?: AssetType;
    }): OrderQtyFormats {
    securityFormats = securityFormats || symbolDecimalsService.getSecurityFormats(security);
    securityInputParams = securityInputParams || symbolDecimalsService.getSecurityInputParams(security);

    if (!qtyAssetType && !orderOrReport) {
        console.error('getOrderQtyFormats: Either qtyAssetType or orderOrReport must be defined.');
    }

    qtyAssetType = qtyAssetType || getOrderQtyAssetType(security, orderOrReport, qtyMultiCurrencyInfo);

    const formats = getOrderQtyCorrectedSecurityFormats({
        security,
        qtyMultiCurrencyInfo,
        securityInputParams,
        normalSecurityFormats: securityFormats,
        qtyAssetType
    });

    const isQtyInBaseQtyAsset = (qtyAssetType === qtyMultiCurrencyInfo.baseQtyAssetType);

    const baseFormats = isQtyInBaseQtyAsset
        ? formats
        : getOrderQtyCorrectedSecurityFormats({
            security,
            qtyMultiCurrencyInfo,
            securityInputParams,
            normalSecurityFormats: securityFormats
        });

    const currencySymbol = getOrderQtyCurrency(security, qtyMultiCurrencyInfo, qtyAssetType);
    const baseCurrencySymbol = getOrderQtyCurrency(security, qtyMultiCurrencyInfo, undefined);

    return {
        formats,
        baseFormats,
        currencySymbol,
        baseCurrencySymbol,
        qtyAssetType,
        qtyMultiCurrencyInfo,
        isQtyInBaseQtyAsset
    };
}
